import React from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { useLang } from "../context/LanguageContext";
import { motion } from "framer-motion";
import { Tag, PiggyBank, Wallet, Sparkles, ArrowRight, ExternalLink, ShieldCheck } from "lucide-react";

const PURCHASE_URL = "https://www.vytalicartes.ca/plans-pricing";
const MEMBER_AREA_URL = "https://app.vytalicartes.ca/";

export default function MembershipPage() {
  const { t } = useLang();

  const benefits = [
    { icon: Tag, title: t("mb.b1t"), desc: t("mb.b1d") },
    { icon: PiggyBank, title: t("mb.b2t"), desc: t("mb.b2d") },
    { icon: Sparkles, title: t("mb.b3t"), desc: t("mb.b3d") },
    { icon: Wallet, title: t("mb.b4t"), desc: t("mb.b4d") },
  ];

  const open = (url) => window.open(url, "_blank", "noopener,noreferrer");

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        path="/membre"
        title="Carte Membre Vytali — 5% de cashback à chaque commande au Québec"
        description="Rejoignez la Carte Membre Vytali et gagnez 5% de cashback sur chaque commande passée chez nos restaurants et commerces partenaires au Québec. 124 promotions exclusives · 2500$ en valeur."
        keywords="carte membre Québec, cashback restaurant Québec, promotions restaurant Québec, Vytali carte, fidélité restaurant"
        image="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?crop=entropy&cs=srgb&fm=jpg&q=90&w=1200&h=630&fit=crop"
      />
      <Header />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#5C5C5C] font-mono">{t("mb.overline")}</p>
            <h1 className="mt-4 text-5xl md:text-6xl font-display font-black tracking-tighter leading-[0.95] text-[#2B2B2B]">
              {t("mb.title")}
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#5C5C5C] max-w-xl leading-relaxed">{t("mb.subtitle")}</p>

            <span data-testid="membership-cashback-badge" className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4A5D23] text-white text-sm font-bold">
              <PiggyBank className="w-4 h-4" /> {t("mb.b3t")}
            </span>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                data-testid="membership-get-card-btn"
                onClick={() => open(PURCHASE_URL)}
                className="flex items-center gap-2 px-7 py-4 rounded-full bg-[#E63946] text-white font-bold hover:scale-[1.02] transition-transform"
              >
                {t("mb.getCard")} <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid="membership-member-area-btn"
                onClick={() => open(MEMBER_AREA_URL)}
                className="flex items-center gap-2 px-6 py-4 rounded-full bg-white border-2 border-[#2B2B2B] text-[#2B2B2B] font-bold hover:bg-[#F4EFE6] transition-colors"
              >
                {t("mb.memberSpace")} <ExternalLink className="w-4 h-4" />
              </button>
            </div>
            <p className="mt-4 flex items-center gap-2 text-sm font-mono text-[#5C5C5C]">
              <ShieldCheck className="w-4 h-4 text-[#4A5D23]" /> {t("mb.securePay")}
            </p>
          </div>

          {/* Virtual card visual */}
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 16, rotate: -2 }}
              animate={{ opacity: 1, y: 0, rotate: -2 }}
              transition={{ duration: 0.5 }}
              className="relative rounded-3xl p-8 bg-[#2B2B2B] text-[#FDFBF7] shadow-[10px_10px_0px_#E63946] aspect-[16/10] flex flex-col justify-between"
            >
              <div className="flex items-center justify-between">
                <span className="text-2xl font-display font-black tracking-tighter">Vytali</span>
                <span className="text-xs font-mono uppercase tracking-widest text-[#F4A261]">Membre</span>
              </div>
              <div className="w-12 h-9 rounded-md bg-gradient-to-br from-[#F4A261] to-[#E63946]" />
              <div>
                <p className="font-mono tracking-[0.3em] text-lg">•••• •••• •••• ••••</p>
                <p className="mt-2 text-xs font-mono text-[#FDFBF7]/70">{t("mb.walletNote")}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((b, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: i * 0.06 }}
              data-testid={`membership-benefit-${i}`}
              className="bg-white border border-[#E5E0D8] rounded-xl p-6"
            >
              <span className="w-11 h-11 rounded-full bg-[#F4EFE6] flex items-center justify-center">
                <b.icon className="w-5 h-5 text-[#E63946]" />
              </span>
              <h3 className="mt-4 text-lg font-display font-bold text-[#2B2B2B]">{b.title}</h3>
              <p className="mt-2 text-sm text-[#5C5C5C] leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="rounded-2xl bg-[#2B2B2B] text-[#FDFBF7] p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-3xl md:text-4xl font-display font-black tracking-tighter">{t("mb.ctaTitle")}</h2>
            <p className="mt-2 text-[#FDFBF7]/80 max-w-xl">{t("mb.ctaText")}</p>
          </div>
          <button
            data-testid="membership-cta-btn"
            onClick={() => open(PURCHASE_URL)}
            className="flex items-center gap-2 px-8 py-4 rounded-full bg-[#E63946] text-white font-bold hover:scale-[1.03] transition-transform shrink-0"
          >
            {t("mb.getCard")} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>
      <Footer />
    </div>
  );
}
